import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button, ErrorToast } from '../../components';
import Spinner from '../../components/common/Spinner';

import { removeUserFromLS } from '../../helpers/localstorage-helper';
import { DASHBOARD, LOGIN, REFERRAL, SECURITY_CODE } from '../../routes/routes';
import {
  continueToAffiliateTerms,
  logoutRemoveState,
  signIn,
  verifySignIn
} from '../../slices/auth-slice';

import { CaptionText, P3 } from '../../styles/FontStyles';
import { AuthLayoutTitle, Flexed } from '../../styles/GlobalComponents';
import { colors } from '../../styles/GlobalStyles';

import { useAccount, useSignMessage, useDisconnect } from 'wagmi';

import { useAppKit } from '@reown/appkit/react';
import { InputBackOffice } from '../../components/common/InputBackoffice';

const captchaId = process.env.REACT_APP_CAPTCHA_ID;

type TParams = { id?: string; referralCode?: string };

const Login = ({ history, match }: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('onboarding');

  const [referrerId, setReferrerId] = useState(Number(match.params.id) ?? null);
  const [isWithReferral, setIsWithReferral] = useState(true);

  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [signatureLoading, setSignatureLoading] = useState(false);

  const [captchaResult, setCaptchaResult] = useState<any>();

  const { open } = useAppKit();

  const dispatch = useAppDispatch();
  let { referralCode }: any = useParams();
  referralCode = referralCode ?? null;

  const { loading } = useAppSelector((state) => state.auth);

  const location = useLocation();

  const { address, isConnected } = useAccount();

  const { disconnect } = useDisconnect();

  const connectWallet = () => {
    setCaptchaLoading(true);
    window?.initGeetest4(
      {
        captchaId,
        language: 'eng',
        product: 'bind',
        riskType: 'slide',
        nativeButton: {
          width: '100%',
          marginBottom: '20px'
        }
      },
      function (captcha: any) {
        captcha.showBox();
        captcha
          .onReady(function () {
            setCaptchaLoading(false);
          })
          .onSuccess(function () {
            const validate = captcha.getValidate();
            setCaptchaResult(validate);

            if (!isConnected) {
              open({ view: 'Connect' });
            }
          })
          .onError(function () {
            setCaptchaLoading(false);
          });
      }
    );
  };

  const { signMessageAsync } = useSignMessage();

  async function safeSignMessage(signMessageAsync: any, message: string) {
    try {
      return await signMessageAsync({ message });
    } catch {
      // Android Chrome → request nestane dok se app prebacuje
      await new Promise((r) => setTimeout(r, 450));
      return await signMessageAsync({ message });
    }
  }

  const getWalletAddresSignature = async () => {
    try {
      setSignatureLoading(true);

      // 1) UZMI CHALLENGE
      const { data } = await axios.get(
        `/authorization/signin/wallet?walletAddress=${address}`
      );

      const message = data.data.challenge;

      // 2) PRICEKAJ DA SE USER VRATI IZ METAMASK U BROWSER
      await new Promise((r) => setTimeout(r, 400));

      // 3) SAFE SIGN (sa retry + delay)
      const signature = await safeSignMessage(signMessageAsync, message);

      // 4) TEK SADA RADI LOGIN
      dispatch(
        signIn({
          result: captchaResult,
          walletAddress: address,
          walletAddressSign: signature,
          history
        })
      );

      setSignatureLoading(false);
      disconnect();
    } catch (error: any) {
      ErrorToast(
        error?.response?.data?.error || 'Signature failed, please try again.'
      );
      disconnect();
      setSignatureLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname === DASHBOARD || location.pathname === LOGIN) {
      setIsWithReferral(false);
    } else {
      setIsWithReferral(true);
    }

    if (isConnected) {
      getWalletAddresSignature();
    }

    setReferrerIdByCodeIfAfiliateRegistration();

    removeUserFromLS();

    dispatch(logoutRemoveState());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location, isConnected, address]);

  const setReferrerIdByCodeIfAfiliateRegistration = async () => {
    if (referralCode !== null) {
      const { data } = await axios.get(`/user/referral-code/${referralCode}`);

      setReferrerId(data.data.user.id);
    }
  };

  return (
    <div>
      {loading && <Spinner fullScreen={true} transparent />}
      {captchaLoading && <Spinner fullScreen={true} transparent />}
      {signatureLoading && <Spinner fullScreen={true} transparent />}

      {!isWithReferral && (
        <AuthLayoutTitle>Sign In with Your Wallet</AuthLayoutTitle>
      )}

      {!isWithReferral && (
        <>
          <Button m='10px 0 20px 0' onClick={() => connectWallet()}>
            Sign in
          </Button>

          <Flexed mt='20' between>
            <CaptionText
              pointer
              semiBold
              center
              mb='30'
              color={colors.primary900}
              onClick={() => history.push(SECURITY_CODE)}>
              Sign In with email
            </CaptionText>

            <CaptionText
              pointer
              semiBold
              center
              mb='30'
              color={colors.primary900}
              onClick={() => history.push(REFERRAL)}>
              {t('login.create_account')}
            </CaptionText>
          </Flexed>
        </>
      )}

      {/* {!isWithReferral && (
        <CaptionText
          pointer
          semiBold
          center
          mb='30'
          color={colors.primary900}
          onClick={() => history.push(REFERRAL)}>
          {t('login.create_account')}
        </CaptionText>
      )} */}

      {isWithReferral && match.params.id && (
        <Button
          onClick={() => {
            dispatch(
              continueToAffiliateTerms({
                referrerId: parseInt(match.params.id || ''),
                history
              })
            );
          }}>
          {t('login.create_account')}
        </Button>
      )}

      {isWithReferral && (
        <CaptionText center m='16px 0 0 0'>
          {t('login.already_have_account')}{' '}
          <span
            style={{
              color: colors.primary900,
              cursor: 'pointer',
              fontWeight: 500
            }}
            onClick={() => history.push('/login')}>
            {t('login.sign_in')}
          </span>
        </CaptionText>
      )}
    </div>
  );
};

export default Login;
